import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import { Button, Field, Input, Label } from "@headlessui/react";
import { PhotoIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
	isAdminRole,
	isHigherThanDistrictAdmin,
	setHasImage,
	setImageHasChanged,
	setUser,
	UserRole,
} from "../../app/userSlice";
import Avatar from "../../components/Avatar";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import { ClassLinkOrCleverIcon } from "../../components/ClassLinkOrCleverIcon";
import Password from "../../components/input/Password";
import RequiredField from "../../components/RequiredField";
import { useGetSelfMutation } from "../users/usersApiSlice";
import {
	MyAccountViewModel,
	useGetAccountInfoQuery,
	useSaveAccountInfoMutation,
	useUploadAvatarMutation,
	useRemoveAvatarMutation,
} from "./accountApiSlice";

export default function MyAccount() {
	const {
		username,
		userId,
		role,
		hasImage,
		course,
		isTeachersWriterAccount,
		requiresAccountConfirmation,
		isImpersonating,
	} = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const result = useGetAccountInfoQuery();
	const accountInfoData = result.data;

	const [updateAccountInfo] = useSaveAccountInfoMutation();
	const [updateUserImage] = useUploadAvatarMutation();
	const [removeUserImage] = useRemoveAvatarMutation();
	const [callGetSelf] = useGetSelfMutation();

	const [accountSaveError, setAccountSaveError] = useState<string>();
	const [isPasswordRequired, setIsPasswordRequired] = useState<boolean>();
	const [firstName, setFirstName] = useState<string>();
	const [lastName, setLastName] = useState<string>();
	const [email, setEmail] = useState<string>();
	const [districts, setDistricts] = useState<string[]>([]);
	const [schools, setSchools] = useState<string[]>([]);
	const [currentPassword, setCurrentPassword] = useState<string>();
	const [newPassword, setNewPassword] = useState<string>();
	const [confirmPassword, setConfirmPassword] = useState<string>();
	const [userImage, setUserImage] = useState<FileList | null>();

	useEffect(() => {
		handleAvatarChange();
	}, [userImage]);

	useEffect(() => {
		setIsPasswordRequired(accountInfoData?.isPasswordRequired);
		setFirstName(accountInfoData?.fName);
		setLastName(accountInfoData?.lName);
		setEmail(accountInfoData?.emailAddress);
		setDistricts(accountInfoData?.districts ?? []);
		setSchools(accountInfoData?.schools ?? []);
	}, [accountInfoData]);

	function handleCancel() {
		(accountInfoData?.isPasswordRequired || requiresAccountConfirmation) && !isImpersonating
			? navigate(`/logout`)
			: handleNavigation();
	}

	function handleSave(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
		e.preventDefault();
		const model = GetUpdateModel();

		if (model) {
			updateAccountInfo(model)
				.unwrap()
				.then(() => {
					callGetSelf()
						.unwrap()
						.then((payload) => {
							dispatch(setUser({ ...payload }));
							handleNavigation();
						})
						.catch((error: any) => {
							console.log(error);
							handleNavigation();
						});
				})
				.catch((error) => {
					setAccountSaveError(error);
					scrollTo(0, 0);
					console.log(error);
				});
		}
	}

	function handleNavigation() {
		isAdminRole(role)
			? navigate(`/adminHome`)
			: !course
				? navigate(`/courses`)
				: navigate(`/home`);
	}

	function handleAvatarChange() {
		setAccountSaveError("");

		if (userImage) {
			// Use formData to upload the user avatar
			const formData = new FormData();
			formData.append("file", userImage[0]);

			updateUserImage(formData)
				.unwrap()
				.then((payload) => {
					dispatch(setHasImage(true));
					dispatch(setImageHasChanged(true));
				})
				.catch((error) => {
					dispatch(setHasImage(false));
					setAccountSaveError(error.data.message);
					scrollTo(0, 0);
				});
		}
	}

	function handleRemoveUserImage() {
		setAccountSaveError("");
		removeUserImage()
			.unwrap()
			.then(() => {
				dispatch(setHasImage(false));
			})
			.catch((error) => {
				setAccountSaveError(error.data.message);
				scrollTo(0, 0);
			});
	}

	function GetUpdateModel(): MyAccountViewModel | null {
		if (!accountInfoData) return null;
		const model: MyAccountViewModel = {
			...accountInfoData,
			fName: firstName ?? "",
			lName: lastName ?? "",
			emailAddress: email,
			currentPassword: currentPassword,
			newPassword: newPassword,
			confirmPassword: confirmPassword,
		};
		return model;
	}

	const tooltipContent = accountInfoData?.isCleverUser
		? "This is a Clever account"
		: accountInfoData?.isClassLinkUser
			? "This is a ClassLink account"
			: undefined;

	return (
		<form>
			<div className="space-y-12">
				{isPasswordRequired && (
					<AlertMessage
						className="mt-4"
						type={AlertLevel.Warning}
						message="Please confirm your account information is correct. You must change your password. Click Save when complete. If there is an error in your account information and editing is enabled, edit fields as required and save. If editing is disabled, please call or write to the help desk for assistance."
						showIcon={true}
					/>
				)}
				<AlertMessage
					className="mt-8"
					type={AlertLevel.Error}
					message={accountSaveError}
					showIcon={true}
					onClear={() => setAccountSaveError(undefined)}
				/>
				<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
					<div>
						<h2 className="text-2xl leading-7 text-gray-900">Account Information</h2>
						{(accountInfoData?.canEditName || accountInfoData?.isEmailAvailable) && (
							<p className="mt-1 text-sm leading-6 text-gray-600">
								<RequiredField /> - Denotes a required field
							</p>
						)}
					</div>

					<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
						<Field className="sm:col-span-4">
							<Label
								htmlFor="username"
								className="block font-medium leading-6 text-gray-900">
								Username
							</Label>
							<div className="mt-2 flex flex-row items-center gap-x-2">
								{(accountInfoData?.isClassLinkUser ||
									accountInfoData?.isCleverUser) && (
									<>
										<span data-tooltip-id="outside-site-icon-tooltip">
											<ClassLinkOrCleverIcon
												isClassLinkEntity={accountInfoData?.isClassLinkUser}
												isCleverEntity={accountInfoData?.isCleverUser}
												iconClasses="size-9"
											/>
										</span>
										<Tooltip
											id="outside-site-icon-tooltip"
											place="top-start"
											content={tooltipContent}
										/>
									</>
								)}
								<div className="read-only:bg-slate-200/75 flex flex-1 rounded-md shadow-sm ring-1 ring-inset ring-gray-300 sm:max-w-md">
									<Input
										type="text"
										name="username"
										id="username"
										className="block flex-1 rounded-md placeholder:text-gray-500 border-0 bg-transparent py-1.5 focus:ring-0 sm:text-sm sm:leading-6"
										placeholder={username ? username : ""}
										readOnly
									/>
								</div>
							</div>
						</Field>

						{role && !isHigherThanDistrictAdmin(role) && (
							<>
								<Field
									className={`${role !== UserRole.DistrictAdmin ? "sm:col-span-3" : "sm:col-span-5"}`}>
									<Label
										htmlFor="district-name"
										className="block font-medium leading-6 text-gray-900">
										District{districts.length > 1 ? "s" : ""}
									</Label>
									<Input
										type="text"
										name="district-name"
										data-tooltip-id="district-tooltip"
										id="district-name"
										className="block w-full mt-2 read-only:bg-slate-200/75 text-gray-500 rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
										value={
											districts.length > 0
												? districts.join(", ")
												: "No district"
										}
										readOnly
									/>
									<Tooltip
										id="district-tooltip"
										className="max-w-[400px]"
										place="bottom-start"
										content={
											districts.length > 0
												? districts.join(", ")
												: "No district"
										}
									/>
								</Field>

								{role !== UserRole.DistrictAdmin && (
									<Field className="sm:col-span-3">
										<Label
											htmlFor="school-name"
											className="block font-medium leading-6 text-gray-900">
											School{schools.length > 1 ? "s" : ""}
										</Label>
										<Input
											type="text"
											name="school-name"
											id="school-name"
											data-tooltip-id="school-tooltip"
											className="block w-full mt-2 read-only:bg-slate-200/75 text-gray-500 rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
											value={
												schools.length ? schools.join(", ") : "No school"
											}
											readOnly
										/>
										<Tooltip
											id="school-tooltip"
											className="max-w-[400px]"
											place="bottom-start"
											content={
												schools.length ? schools.join(", ") : "No school"
											}
										/>
									</Field>
								)}
							</>
						)}

						<Field className="sm:col-span-3">
							<Label
								htmlFor="first-name"
								className="block font-medium leading-6 text-gray-900">
								First Name
								{accountInfoData?.canEditName ? <RequiredField /> : ""}
							</Label>
							<Input
								type="text"
								name="first-name"
								id="first-name"
								className="block w-full mt-2 read-only:bg-slate-200/75 read-only:text-gray-500 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
								readOnly={!accountInfoData?.canEditName}
								required={accountInfoData?.canEditName}
								maxLength={accountInfoData?.rules.firstNameMaxLength}
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
							/>
						</Field>

						<Field className="sm:col-span-3">
							<Label
								htmlFor="last-name"
								className="block font-medium leading-6 text-gray-900">
								Last Name
								{accountInfoData?.canEditName ? <RequiredField /> : ""}
							</Label>
							<Input
								type="text"
								name="last-name"
								id="last-name"
								autoComplete="family-name"
								className="block w-full mt-2 read-only:bg-slate-200/75 read-only:text-gray-500 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
								readOnly={!accountInfoData?.canEditName}
								required={accountInfoData?.canEditName}
								maxLength={accountInfoData?.rules.lastNameMaxLength}
								value={`${lastName}${isTeachersWriterAccount ? " (Student)" : ""}`}
								onChange={(e) => setLastName(e.target.value)}
							/>
						</Field>

						{accountInfoData?.isEmailAvailable ? (
							<Field className="sm:col-span-4">
								<Label
									htmlFor="email"
									className="block font-medium leading-6 text-gray-900">
									Email Address
									<RequiredField />
								</Label>
								<Input
									id="email"
									name="email"
									type="email"
									value={email}
									required
									maxLength={accountInfoData?.rules.emailMaxLength}
									onChange={(e) => setEmail(e.target.value)}
									className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
								/>
							</Field>
						) : null}

						{config?.enableAvatarImages && (
							<Field className="col-span-full">
								<Label
									htmlFor="photo"
									className="flex font-medium leading-6 text-gray-900">
									User Image
								</Label>
								{accountInfoData?.imageUploadDisabled ? null : (
									<p className="mt-1 text-sm leading-6 text-gray-600">
										Images must be 50x50 in size and be a JPG file.
									</p>
								)}
								<div className="mt-2 flex items-center gap-x-3">
									<div className="relative inline-block">
										<Avatar
											className="h-14 w-14 block"
											hasImage={hasImage ?? false}
											initials={accountInfoData?.initials ?? ""}
											userId={userId ?? 0}
											override={true}
										/>
										{hasImage ? (
											<XCircleIcon
												className="mx-auto h-8 w-8 fill-red-700 stroke-white absolute -bottom-2 -right-2 hover:cursor-pointer"
												onClick={() => handleRemoveUserImage()}
											/>
										) : null}
									</div>

									{accountInfoData?.imageUploadDisabled ? (
										<p className="mt-1 leading-6 text-gray-600">
											Uploading a user image is forbidden.{" "}
										</p>
									) : (
										<Button
											disabled={accountInfoData?.imageUploadDisabled}
											className="btn btn-neutral">
											<Label className="flex hover:cursor-pointer">
												<PhotoIcon
													className="mx-auto h-6 w-6"
													aria-hidden="true"
												/>
												<span className="flex pl-2 items-center">
													Change Avatar
												</span>
												<Input
													className="sr-only"
													type="file"
													multiple={false}
													accept=".jpg, .jpeg"
													onChange={(e) => setUserImage(e.target.files)}
													onClick={(e) => (e.currentTarget.value = "")}
												/>
											</Label>
										</Button>
									)}
								</div>
							</Field>
						)}
					</div>
				</div>

				<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
					<div>
						<h2 className="text-2xl leading-7 text-gray-900">Change Your Password</h2>
						{isPasswordRequired ? (
							<p className="mt-1 text-sm leading-6 text-gray-600">
								<RequiredField /> - Denotes a required field
							</p>
						) : (
							<p className="mt-1 mr-24 text-sm leading-6 text-gray-600">
								You must provide your current password to change your password.
							</p>
						)}
						<p className="mt-1 text-sm leading-6 text-gray-600">
							Your new password must be at least{" "}
							{accountInfoData?.minimumPasswordLength} characters long.
						</p>
					</div>

					<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
						{!isPasswordRequired && (
							<Field className="sm:col-span-4">
								<Label
									htmlFor="current-password"
									className="block font-medium leading-6 text-gray-900">
									Current Password
								</Label>
								<Password
									id="current-password"
									name="current-password"
									maxLength={accountInfoData?.rules.passwordMaxLength}
									placeholder="current password"
									className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
									onChange={(e) => setCurrentPassword(e.target.value)}
									value={currentPassword ?? ""}
								/>
							</Field>
						)}

						<Field className="sm:col-span-4">
							<Label
								htmlFor="new-password"
								className="block font-medium leading-6 text-gray-900">
								New Password
								{isPasswordRequired ? <RequiredField /> : ""}
							</Label>
							<Password
								id="new-password"
								name="new-password"
								maxLength={accountInfoData?.rules.passwordMaxLength}
								required={isPasswordRequired}
								placeholder="new password"
								className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
								onChange={(e) => setNewPassword(e.target.value)}
								value={newPassword ?? ""}
							/>
						</Field>

						<Field className="sm:col-span-4">
							<Label
								htmlFor="confirm-password"
								className="block font-medium leading-6 text-gray-900">
								Confirm Password
								{isPasswordRequired ? <RequiredField /> : ""}
							</Label>
							<Password
								id="confirm-password"
								name="confirm-password"
								maxLength={accountInfoData?.rules.passwordMaxLength}
								required={isPasswordRequired}
								placeholder="confirm password"
								className="block w-full mt-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
								onChange={(e) => setConfirmPassword(e.target.value)}
								value={confirmPassword ?? ""}
							/>
						</Field>
					</div>
				</div>
			</div>

			<div className="mt-6 flex items-center justify-end gap-x-6">
				<Button
					type="submit"
					className="btn btn-primary"
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSave(e)}>
					Save
				</Button>
				<Button type="button" className="btn btn-naked" onClick={handleCancel}>
					Cancel
				</Button>
			</div>
		</form>
	);
}
